import React, { ChangeEvent, useState } from "react"
import {
    TextField,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    SelectChangeEvent,
    FormHelperText,
} from "@mui/material"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import CloseIcon from "@mui/icons-material/Close"
import FileUploadIcon from "@mui/icons-material/FileUpload"
interface Meta {
    name?: string
    displayName?: string
    severity?: string
    options?: { label: string; value: string }[]
    validation: {
        required?: boolean
    }
    dataType?: string
}
export interface Field {
    name: string
    meta: Meta
}
interface DataState {
    name?: string
    displayName?: string
    severity?: string
    text: string
    files: File[]
    dataType?: string
}
interface FileWithInputProps {
    data: Field
    onChange: (data: DataState) => void
    error?: { hasError: boolean; errorMsg?: string }
    field?: { value: string | null }
}
const FileWithInput = ({ data, onChange, error, field }: FileWithInputProps) => {
    const [dataState, setDataState] = useState<DataState>({
        name: data?.name,
        displayName: data?.meta?.displayName,
        severity: data?.meta?.severity,
        text: "",
        files: [],
    })
    const [filePreviews, setFilePreviews] = useState<string[]>([])
    const [dismissedError, setDismissedError] = useState(false)
    const uniqueFileInputId = `file-upload-input-${data.name}`
    const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newTextValue = e.target.value
        setDataState((prevState) => ({ ...prevState, text: newTextValue }))
        validateField(newTextValue, dataState.files)
    }
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : []
        setDataState((prevState) => ({ ...prevState, files }))
        if (files.length > 0) {
            const newPreviews = files.map((file) => URL.createObjectURL(file))
            setFilePreviews(newPreviews)
        }
        validateField(dataState.text, files)
    }
    const handleRemoveImage = () => {
        setDataState((prevState) => ({ ...prevState, files: [] }))
        setFilePreviews([])
        validateField(dataState.text, [])
    }
    const handleActionChange = (e: SelectChangeEvent) => {
        const action = e.target.value as string
        if (action === "Upload") {
            document.getElementById(uniqueFileInputId)?.click()
        } else if (action === "Remove") {
            handleRemoveImage()
        }
    }

    const validateField = (text: string, files: File[]) => {
        const { required } = data.meta.validation || {}
        if (!required) return

        const hasValidationError = text.trim() === "" || files.length === 0
        setDismissedError(!hasValidationError)

        if (field) {
            field.value = hasValidationError ? null : "valid"
            if (error) error.hasError = hasValidationError
        }
    }

    React.useEffect(() => {
        onChange(dataState)
    }, [dataState, onChange])

    return (
        <>
            <Grid item xs={12}>
                <InputLabel sx={{ mb: 1.5 }}>
                    {`${data?.meta?.displayName}${data?.meta?.validation?.required ? " *" : ""}`}
                </InputLabel>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={dataState.text}
                    onChange={handleTextChange}
                    error={error?.hasError && !dismissedError} // Conditional error display
                />
            </Grid>
            <Grid item xs={12}>
                {filePreviews.length > 0 && (
                    <Grid container spacing={1} direction="column">
                        {filePreviews.map((preview, index) => (
                            <Grid item key={index} textAlign="center">
                                <img
                                    src={preview}
                                    alt={`Preview ${index + 1}`}
                                    style={{
                                        width: "250px",
                                        height: "150px",
                                        maxWidth: "250px",
                                        maxHeight: "170px",
                                        marginTop: "10px",
                                        marginBottom: "10px",
                                        borderRadius: "10px",
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
                <Select
                    variant="outlined"
                    value={"FILE"}
                    onChange={handleActionChange}
                    fullWidth
                    size="small"
                    renderValue={() => (
                        <Typography
                            variant="body1"
                            sx={{
                                ml: { sm: 3, xs: 2, md: 3, lg: 3 },
                                color: filePreviews.length > 0 ? "#061F1B" : "#07405E",
                                lineHeight: 4.5,
                                fontWeight: filePreviews.length > 0 ? 590 : 400,
                            }}
                        >
                            {`${filePreviews.length} FILE(S) SELECTED`}
                        </Typography>
                    )}
                    startAdornment={<CloudUploadIcon />}
                    IconComponent={() => <ArrowDownwardIcon sx={{ mr: 1 }} />}
                    sx={{
                        height: "46px",
                        borderRadius: 2,
                        border: "2px solid #ffffff",
                        marginTop: "15px",
                        textAlign: "center",
                        "&.Mui-focused": {
                            border: "0.5px solid transparent",
                        },
                    }}
                >
                    <MenuItem value="Upload" sx={{ display: "flex", justifyContent: "space-between" }}>
                        Upload
                        <FileUploadIcon sx={{ color: "#1976D2" }} />
                    </MenuItem>
                    <MenuItem value="Remove" sx={{ display: "flex", justifyContent: "space-between" }}>
                        Remove
                        <CloseIcon sx={{ color: "#D32F2F" }} />
                    </MenuItem>
                </Select>
                <input
                    hidden
                    accept="image/*,video/*"
                    multiple
                    type="file"
                    id={uniqueFileInputId}
                    onChange={handleFileChange}
                />
                {error?.hasError && !dismissedError && (
                    <Grid item xs={12} ml={2}>
                        <FormHelperText error>{error?.errorMsg}</FormHelperText>
                    </Grid>
                )}
            </Grid>
        </>
    )
}
export default FileWithInput
